import React from "react";
import ReactDOM from "react-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Popper,
  PopperProps,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faExpand, faEye, faPaperclip, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import PDFViewer from "components/PDFViewer";
import registryInvoicesService, {
  ICreateRegistryInvoicePayload,
} from "api/registryInvoicesService";
import postingService, {
  IPostingLine,
  IPostingRequest,
  IPostingResult,
} from "api/postingService";
import postingSchemeService, {
  IPostingScheme,
  IPostingSchemeLine,
  PostingSchemeBase,
  PostingSchemeDirection,
  SCHEMES_QUERY_KEY,
  SCHEMES_RESOLVE_QUERY_KEY,
} from "api/postingSchemeService";
import { useQueryClient } from "react-query";
import {
  RegistryInvoiceDocumentType,
  RegistryInvoiceStatus,
} from "dto/Invoices/IRegistryInvoice";
import { IRegistryInvoicePrefill } from "dto/Invoices/IRegistryInvoicePrefill";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import usePDF from "hooks/usePDF";
import { axiosInstance } from "api/axios";
import CollapsibleSection from "./CollapsibleSection";
import AmountField from "./AmountField";
import parseAmount from "./parseAmount";
import { NIPMaskedInput, AccountNumberMaskedInput } from "./MaskedInputs";
import { loadWpkTree, flattenWpk, IWpkFlat } from "data/wpk_gminy";
import styles from "./styles.module.scss";

const wpkFilter = createFilterOptions<IWpkFlat>({
  stringify: (o) => `${o.code} ${o.label} ${o.path}`,
});

const WidePopper = (props: PopperProps) => {
  const anchor = props.anchorEl as HTMLElement | null;
  const width = anchor ? anchor.clientWidth * 3 : undefined;
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[{ name: "flip", enabled: false }]}
      style={{ ...props.style, width, maxWidth: "90vw" }}
    />
  );
};

const NIP_REGEX = /^\d{10}$/;

const VAT_RATES = [0, 5, 8, 23];

const CURRENCIES = [
  "PLN", "EUR", "USD", "GBP", "CHF", "CZK", "NOK", "SEK", "DKK", "HUF", "RON", "BGN", "UAH",
];

// Rodzaje dziennika / rejestru księgowego (kod → nazwa). Docelowo z konfiguracji BE.
const JOURNAL_OPTIONS: { value: string; label: string }[] = [
  { value: "ZAK", label: "ZAK — Rejestr zakupu" },
  { value: "SPR", label: "SPR — Rejestr sprzedaży" },
  { value: "KAS", label: "KAS — Raport kasowy" },
  { value: "BAN", label: "BAN — Wyciąg bankowy" },
  { value: "PK", label: "PK — Polecenie księgowania" },
];

const STATUS_OPTIONS: { value: RegistryInvoiceStatus; label: string }[] = [
  { value: "AwaitingPayment", label: "Oczekuje na płatność" },
  { value: "Paid", label: "Zapłacona" },
  { value: "Overdue", label: "Przeterminowana" },
  { value: "Cancelled", label: "Anulowana" },
];

interface AmountRow {
  id: string;
  net: string;
  vatRate: number;
  vat: string;
  gross: string;
}

// Pojedynczy wiersz dekretu księgowego (zapis na konta Wn/Ma).
interface DecreeLine {
  id: string;
  accountCode: string; // Nr kont (WPK)
  accountDesc: string; // Opis — auto z WPK
  kwotaWn: string;     // Konto Wn
  kwotaMa: string;     // Konto Ma
  description: string; // textarea Opis
}

const fmtPLN = (n: number): string =>
  new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const recalcRow = (row: AmountRow): AmountRow => {
  const net = parseAmount(row.net);
  if (!net) return { ...row, vat: "", gross: "" };
  const vatAmt = Math.round(net * row.vatRate) / 100;
  return { ...row, vat: fmtPLN(vatAmt), gross: fmtPLN(net + vatAmt) };
};

const makeRow = (): AmountRow => ({
  id: Math.random().toString(36).slice(2),
  net: "",
  vatRate: 23,
  vat: "",
  gross: "",
});

const ReadAmountField = ({
  label,
  value,
  sx,
}: {
  label: string;
  value: string;
  sx?: object;
}) => (
  <TextField
    label={label}
    size="small"
    value={value}
    InputProps={{
      readOnly: true,
      endAdornment: <InputAdornment position="end">zł</InputAdornment>,
    }}
    inputProps={{ style: { textAlign: "right" } }}
    sx={sx}
  />
);

const InvoiceRegistryForm = ({
  onClose,
  preloadedFile,
  hideFileSection,
  prefill,
  onBookNext,
  onBooked,
}: {
  onClose: () => void;
  preloadedFile?: { blob: Blob; fileName: string } | null;
  hideFileSection?: boolean;
  prefill?: IRegistryInvoicePrefill;
  /** Przejście do księgowania następnej faktury (tryb „Księgowanie"). */
  onBookNext?: () => void;
  /** Oznaczenie tej faktury jako zaksięgowanej (pomijanie w kolejce). */
  onBooked?: () => void;
}) => {
  const [documentType, setDocumentType] =
    useState<RegistryInvoiceDocumentType | null>(hideFileSection ? "Invoice" : null);

  const [openSection, setOpenSection] = useState<string | null>(
    preloadedFile || hideFileSection ? "issuer" : "file"
  );
  const toggleSection = (key: string) =>
    setOpenSection((prev) => (prev === key ? null : key));

  // File
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const { blob, addBlob, removeBlob } = usePDF();

  // Pre-populate file when opened from PDF preview
  React.useEffect(() => {
    if (!preloadedFile) return;
    const { blob: srcBlob, fileName } = preloadedFile;
    const file = new File([srcBlob], fileName, {
      type: srcBlob.type || "application/pdf",
    });
    setInvoiceFile(file);
    addBlob(srcBlob, fileName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invoice issuer
  const [issuerNIP, setIssuerNIP] = useState("");
  const [issuerCompany, setIssuerCompany] = useState("");
  const [nipLoading, setNipLoading] = useState(false);

  // Receipt issuer
  const [issuerPESEL, setIssuerPESEL] = useState("");
  const [issuerFullName, setIssuerFullName] = useState("");

  // Shared issuer
  const [issuerAddress, setIssuerAddress] = useState("");
  const [issuerEmail, setIssuerEmail] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // Invoice-specific data
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [currency, setCurrency] = useState("PLN");
  const [amountRows, setAmountRows] = useState<AmountRow[]>([makeRow()]);
  // Receipt-specific data
  const [amount, setAmount] = useState("");

  // Common data
  const [issueDate, setIssueDate] = useState<moment.Moment | null>(moment());
  const [paymentDate, setPaymentDate] = useState<moment.Moment | null>(null);
  const [subject, setSubject] = useState("");
  const [status, setStatus] = useState<RegistryInvoiceStatus | "">("");

  // Tryb „Księgowanie": wstępne wypełnienie sekcji Wystawca + Dane faktury
  // danymi z faktury KSeF (XML). Wystawca = sprzedawca z faktury.
  React.useEffect(() => {
    if (!prefill) return;
    setDocumentType("Invoice");
    if (prefill.IssuerNIP) setIssuerNIP(prefill.IssuerNIP);
    if (prefill.IssuerCompany) setIssuerCompany(prefill.IssuerCompany);
    if (prefill.IssuerAddress) setIssuerAddress(prefill.IssuerAddress);
    if (prefill.IssuerEmail) setIssuerEmail(prefill.IssuerEmail);
    if (prefill.AccountNumber) setAccountNumber(prefill.AccountNumber);
    if (prefill.InvoiceNumber) setInvoiceNumber(prefill.InvoiceNumber);
    if (prefill.Currency) setCurrency(prefill.Currency);
    if (prefill.IssueDate) setIssueDate(moment(prefill.IssueDate));
    if (prefill.PaymentDate) setPaymentDate(moment(prefill.PaymentDate));

    const { NetAmount: net, VatAmount: vat, GrossAmount: gross } = prefill;
    if (net != null || vat != null || gross != null) {
      const fmt = (n?: number) => (n != null ? n.toFixed(2) : "");
      const vatRate = net && vat ? Math.round((vat / net) * 100) : 23;
      setAmountRows([
        {
          id: Math.random().toString(36).slice(2),
          net: fmt(net),
          vatRate,
          vat: fmt(vat),
          gross: fmt(gross),
        },
      ]);
    }

    if (prefill.ItemNames && prefill.ItemNames.length > 0) {
      const subject =
        prefill.ItemNames.length > 1
          ? `${prefill.ItemNames[0]} i inne`
          : prefill.ItemNames[0];
      setSubject(subject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- PDF → field paste ----
  const pdfPanelRef = useRef<HTMLDivElement>(null);
  const [pdfSelection, setPdfSelection] = useState<string>("");

  useEffect(() => {
    const handler = () => {
      const sel = document.getSelection();
      const text = sel?.toString().trim() ?? "";
      if (!text || !pdfPanelRef.current || !sel || sel.rangeCount === 0) {
        setPdfSelection("");
        return;
      }
      try {
        if (pdfPanelRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
          setPdfSelection(text);
        } else {
          setPdfSelection("");
        }
      } catch {
        setPdfSelection("");
      }
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  // Returns onMouseDown handler that pastes the active PDF selection into `setter`
  const pasteProps = (setter: (v: string) => void) => ({
    onMouseDown: () => {
      if (!pdfPanelRef.current) return;
      const sel = document.getSelection();
      const text = sel?.toString().trim() ?? "";
      if (!text || !sel || sel.rangeCount === 0) return;
      try {
        if (pdfPanelRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
          setter(text);
          setPdfSelection("");
          sel.removeAllRanges();
        }
      } catch {}
    },
  });

  // ── Etap: Dekretacja księgowa ─────────────────────────────────────────────
  const [postingDate, setPostingDate] = useState<moment.Moment | null>(null);
  const [operationDate, setOperationDate] = useState<moment.Moment | null>(null);
  // Czy wykonano operację księgowania — dopiero wtedy pokazujemy Datę
  // księgowania i Numer dowodu księgowego.
  const [isPosted, setIsPosted] = useState(false);
  const [journal, setJournal] = useState("ZAK"); // rodzaj dziennika / rejestru
  const [accountingDocNo, setAccountingDocNo] = useState(""); // nr dowodu księgowego
  const makeDecreeLine = (): DecreeLine => ({
    id: crypto.randomUUID(),
    accountCode: "",
    accountDesc: "",
    kwotaWn: "",
    kwotaMa: "",
    description: "",
  });
  const [decreeLines, setDecreeLines] = useState<DecreeLine[]>([
    makeDecreeLine(),
    makeDecreeLine(),
  ]);
  const addDecreeLine = () => setDecreeLines((l) => [...l, makeDecreeLine()]);
  const removeDecreeLine = (id: string) =>
    setDecreeLines((l) => (l.length > 1 ? l.filter((x) => x.id !== id) : l));
  const updateDecreeLine = (id: string, patch: Partial<DecreeLine>) =>
    setDecreeLines((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  // Suma Wn / Ma — dekret musi się bilansować (Wn = Ma).
  const decreeSum = decreeLines.reduce(
    (acc, x) => ({
      dt: acc.dt + parseAmount(x.kwotaWn),
      ct: acc.ct + parseAmount(x.kwotaMa),
    }),
    { dt: 0, ct: 0 },
  );
  const decreeBalanced =
    Math.round(decreeSum.dt * 100) === Math.round(decreeSum.ct * 100);
  // Kwota do rozksięgowania = Kwota netto (z „Dane faktury") − suma kwot Wn.
  const invoiceNetTotal =
    documentType === "Receipt"
      ? parseAmount(amount)
      : amountRows.reduce((s, r) => s + parseAmount(r.net), 0);
  const invoiceVatTotal =
    documentType === "Receipt"
      ? 0
      : amountRows.reduce((s, r) => s + parseAmount(r.vat), 0);
  const invoiceGrossTotal =
    documentType === "Receipt"
      ? parseAmount(amount)
      : amountRows.reduce((s, r) => s + parseAmount(r.gross), 0);
  const decreeAmountsSum = decreeLines.reduce(
    (s, x) => s + parseAmount(x.kwotaWn),
    0,
  );
  const amountToPost = invoiceNetTotal - decreeAmountsSum;
  // Opcje pola „Konto Wn" ze słownika WPK gminy (Ustawienia → Słowniki).
  const [wpkOptions] = useState(() => flattenWpk(loadWpkTree()));
  // Tooltip „Konto Wn": numer + nazwa konta (lub sama wartość).
  const wpkTooltip = (value: string) => {
    const found = wpkOptions.find((o) => o.code === value || o.label === value);
    if (!found) return value;
    return found.code ? `${found.code} — ${found.label}` : found.label;
  };
  // Pole „Opis" jest kompaktowe; pełny textarea otwiera się na życzenie (popover).
  const [descEditor, setDescEditor] = useState<{
    id: string;
    anchor: HTMLElement;
  } | null>(null);

  const { mutate: createInvoice, isLoading } = registryInvoicesService.useCreateInvoice();
  const { mutate: simulatePosting, isLoading: isSimulating } = postingService.useSimulate();
  const { mutate: bufferPosting, isLoading: isBuffering } = postingService.useBuffer();
  const { mutate: commitPosting, isLoading: isCommitting } = postingService.useCommit();
  const { mutate: saveScheme } = postingSchemeService.useSaveScheme();
  const queryClient = useQueryClient();
  // Kierunek schematu wg roli kontrahenta na dokumencie: kontrahent jako
  // sprzedawca (faktura zakupowa) → „Purchase"; jako nabywca (sprzedaż) → „Sale".
  const nipClean = issuerNIP.replace(/\D/g, "");
  const postingDirection: PostingSchemeDirection =
    journal === "SPR" ? "Sale" : "Purchase";
  const schemesQuery = postingSchemeService.useResolveSchemes(
    nipClean,
    postingDirection,
    documentType === "Invoice" && nipClean.length === 10,
  );
  const availableSchemes = schemesQuery.data ?? [];
  const hasScheme = availableSchemes.length > 0;
  // Schemat zastosowany przyciskiem „Dekretuj" (do wykrycia ręcznej zmiany).
  const [appliedScheme, setAppliedScheme] = useState<IPostingScheme | null>(null);
  // Anchor menu wyboru schematu (gdy jest kilka) oraz modal „nowy/aktualizuj".
  const [schemeMenuAnchor, setSchemeMenuAnchor] = useState<HTMLElement | null>(null);
  const [divergeOpen, setDivergeOpen] = useState(false);
  // Wynik ostatecznego księgowania (commit) — po sukcesie pokazujemy wpis
  // w dzienniku księgowania i przyciski nawigacji zamiast akcji księgowania.
  const [postingResult, setPostingResult] = useState<IPostingResult | null>(null);
  const isBooked = postingResult?.status === "Posted";
  const busy = isLoading || isSimulating || isBuffering || isCommitting;

  const handleNipBlur = async (value: string) => {
    const clean = value.replace(/[-\s]/g, "");
    if (!NIP_REGEX.test(clean)) return;
    if (issuerCompany.trim() && issuerAddress.trim()) return;
    setNipLoading(true);
    try {
      const res = await axiosInstance.get(`/bir/search?nip=${clean}`);
      const data = res?.data ?? {};
      if (!issuerCompany.trim()) {
        const name: string = data.Name ?? "";
        if (name) setIssuerCompany(name);
      }
      if (!issuerAddress.trim()) {
        const street: string = data.Street ?? "";
        const house: string = data.HouseNumber ? String(data.HouseNumber) : "";
        const local: string = data.LocalNumber ? String(data.LocalNumber) : "";
        const zip: string = data.ZipCode ?? "";
        const place: string = data.Place ?? "";
        if (street || place) {
          const streetLine = [
            street ? `ul. ${street}` : "",
            house ? (local ? `${house}/${local}` : house) : "",
          ]
            .filter(Boolean)
            .join(" ");
          const cityLine = [zip, place].filter(Boolean).join(" ");
          const address = [streetLine, cityLine].filter(Boolean).join(", ");
          if (address) setIssuerAddress(address);
        }
      }
    } catch {
      // silent — user can type manually
    } finally {
      setNipLoading(false);
    }
  };

  const updateRow = (id: string, field: keyof AmountRow, value: string | number) => {
    setAmountRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        if (field === "net" || field === "vatRate") return recalcRow(updated);
        return updated;
      })
    );
  };

  const addRow = () => setAmountRows((prev) => [...prev, makeRow()]);

  const removeRow = (id: string) =>
    setAmountRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (!accepted.length) return;
      const file = accepted[0];
      setInvoiceFile(file);
      addBlob(new Blob([file], { type: file.type }), file.name);
    },
    [addBlob]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  const canSubmit = documentType !== null && !!issueDate?.isValid();

  const apiErrText = (err: unknown, fallback: string): string => {
    type ApiErr = {
      response?: { data?: { message?: string; title?: string } };
      message?: string;
    };
    const e = err as ApiErr;
    return (
      e?.response?.data?.message ??
      e?.response?.data?.title ??
      e?.message ??
      fallback
    );
  };

  const buildInvoicePayload = (): ICreateRegistryInvoicePayload => {
    const totalNet = amountRows.reduce((s, r) => s + parseAmount(r.net), 0);
    const totalVat = amountRows.reduce((s, r) => s + parseAmount(r.vat), 0);
    const totalGross = amountRows.reduce((s, r) => s + parseAmount(r.gross), 0);

    const isPartial =
      documentType === "Invoice"
        ? !invoiceNumber.trim() || !issuerNIP.trim() || totalGross === 0
        : !issuerPESEL.trim() || !issuerFullName.trim() || parseAmount(amount) === 0;

    return {
      DocumentType: documentType!,
      IssueDate: issueDate!.toISOString(),
      ...(paymentDate?.isValid() && { PaymentDate: paymentDate.toISOString() }),
      ...(subject.trim() && { Subject: subject.trim() }),
      ...(status && { Status: status }),
      ...(currency && { Currency: currency }),
      ...(accountNumber.trim() && { AccountNumber: accountNumber.replace(/\D/g, "") }),
      ...(issuerAddress.trim() && { IssuerAddress: issuerAddress.trim() }),
      ...(issuerEmail.trim() && { IssuerEmail: issuerEmail.trim() }),
      ...(documentType === "Invoice" && {
        ...(invoiceNumber.trim() && { InvoiceNumber: invoiceNumber.trim() }),
        ...(issuerNIP.trim() && { IssuerNIP: issuerNIP.replace(/\D/g, "") }),
        ...(issuerCompany.trim() && { IssuerCompany: issuerCompany.trim() }),
        ...(totalNet && { NetAmount: totalNet }),
        ...(totalVat && { VatAmount: totalVat }),
        ...(totalGross && { GrossAmount: totalGross }),
      }),
      ...(documentType === "Receipt" && {
        ...(issuerPESEL.trim() && { IssuerPESEL: issuerPESEL.trim() }),
        ...(issuerFullName.trim() && { IssuerFullName: issuerFullName.trim() }),
        ...(amount && { Amount: parseAmount(amount) }),
      }),
      ...(isPartial && { IsPartial: true }),
      file: invoiceFile,
    };
  };

  // Dekret → wiersze księgowe (pomijamy puste wiersze).
  const buildPostingLines = (): IPostingLine[] =>
    decreeLines
      .filter((l) => l.accountCode.trim() || parseAmount(l.kwotaWn) || parseAmount(l.kwotaMa))
      .map((l) => ({
        accountCode: l.accountCode.trim(),
        description: l.description.trim() || l.accountDesc || undefined,
        amountDt: parseAmount(l.kwotaWn),
        amountCt: parseAmount(l.kwotaMa),
      }));

  const buildPostingRequest = (registryInvoiceId?: string): IPostingRequest => ({
    registryInvoiceId,
    journal: journal || undefined,
    operationDate: operationDate?.isValid() ? operationDate.toISOString() : undefined,
    postingDate: postingDate?.isValid() ? postingDate.toISOString() : undefined,
    documentNumber: accountingDocNo.trim() || invoiceNumber.trim() || undefined,
    netAmount: invoiceNetTotal,
    vatAmount: invoiceVatTotal,
    grossAmount: invoiceGrossTotal,
    lines: buildPostingLines(),
  });

  // Symulacja — walidacja dekretu bez zapisu (BE nic nie utrwala).
  const handleSimulate = () => {
    simulatePosting(buildPostingRequest(), {
      onSuccess: (result) =>
        notification({
          title: "Symulacja księgowania",
          text: result.balanced
            ? `Dekret bilansuje się (Wn = Ma = ${fmtPLN(result.totalDt)}). Proponowany wpis: ${result.entryNumber ?? "—"}.`
            : `Dekret nie bilansuje się — różnica ${fmtPLN(Math.abs(result.difference))}.`,
          type: result.balanced ? ENotificationType.INFO : ENotificationType.ERROR,
        }),
      onError: (err) =>
        notification({
          title: "Symulacja księgowania",
          text: apiErrText(err, "Nie udało się wykonać symulacji."),
          type: ENotificationType.ERROR,
        }),
    });
  };

  // Bufor — zapis prowizoryczny (odwracalny); odsłania pola dowodu księgowego.
  const handleBuffer = () => {
    bufferPosting(buildPostingRequest(), {
      onSuccess: (result) => {
        setIsPosted(true);
        if (result.entryDate) setPostingDate(moment(result.entryDate));
        notification({
          title: "Bufor księgowania",
          text: `Dekret zapisano w buforze księgowania (${result.entryNumber ?? "—"}).`,
          type: ENotificationType.INFO,
        });
      },
      onError: (err) =>
        notification({
          title: "Bufor księgowania",
          text: apiErrText(err, "Nie udało się zapisać do bufora księgowania."),
          type: ENotificationType.ERROR,
        }),
    });
  };

  // Właściwe zaksięgowanie: zapis faktury do rejestru + commit dekretu.
  // Slajd NIE jest zamykany — pokazujemy wpis w dzienniku i nawigację.
  const doCommit = () => {
    createInvoice(buildInvoicePayload(), {
      onSuccess: (created) => {
        commitPosting(buildPostingRequest(created.Id), {
          onSuccess: (result) => {
            setPostingResult(result);
            setIsPosted(true);
            if (result.entryDate) setPostingDate(moment(result.entryDate));
            onBooked?.();
            notification({
              title: "Zaksięgowano",
              text: `Wpis ${result.entryNumber ?? ""} w dzienniku ${result.journalName ?? result.journal}.`,
              type: ENotificationType.INFO,
            });
          },
          onError: (err) =>
            notification({
              title: "Błąd księgowania",
              text: apiErrText(err, "Faktura zapisana, ale nie udało się zaksięgować dekretu."),
              type: ENotificationType.ERROR,
            }),
        });
      },
      onError: (err) =>
        notification({
          title: "Błąd",
          text: apiErrText(err, "Nie udało się dodać dokumentu do rejestru."),
          type: ENotificationType.ERROR,
        }),
    });
  };

  // Wiersze dekretu → wiersze schematu (do porównania i zapisu jako schemat).
  const decreeToSchemeLines = (): IPostingSchemeLine[] => {
    const net = amountToPost;
    const vat = invoiceVatTotal;
    const gross = net + vat;
    const baseOf = (amt: number): PostingSchemeBase =>
      Math.abs(amt - gross) < 0.01 ? "Gross" : vat > 0 && Math.abs(amt - vat) < 0.01 ? "Vat" : "Net";
    const baseVal = (b: PostingSchemeBase) => (b === "Gross" ? gross : b === "Vat" ? vat : net);
    const mk = (side: "Dt" | "Ct", acc: string, amt: number, desc: string): IPostingSchemeLine => {
      const b = baseOf(amt);
      const bv = baseVal(b);
      return { side, account: acc, base: b, factor: bv ? Math.round((amt / bv) * 10000) / 10000 : 1, description: desc || undefined };
    };
    const out: IPostingSchemeLine[] = [];
    decreeLines.forEach((l) => {
      const acc = l.accountCode.trim();
      if (!acc) return;
      const wn = parseAmount(l.kwotaWn);
      const ma = parseAmount(l.kwotaMa);
      if (wn > 0) out.push(mk("Dt", acc, wn, l.description));
      if (ma > 0) out.push(mk("Ct", acc, ma, l.description));
    });
    return out;
  };

  const schemeSig = (lines: IPostingSchemeLine[]) =>
    lines.map((l) => `${l.side}|${l.account}|${l.base}`).sort().join(";");

  // Czy dekret różni się od zastosowanego/zapisanego schematu (ręczna zmiana).
  const isDiverged = (): boolean => {
    const cur = decreeToSchemeLines();
    if (cur.length === 0) return false;
    if (appliedScheme) return schemeSig(cur) !== schemeSig(appliedScheme.lines);
    return true; // dekret ręczny bez zastosowania schematu
  };

  // Zaksięguj — jeśli dekret różni się od schematu, pytamy o zapis schematu.
  const handleBook = () => {
    if (!canSubmit) return;
    if (isDiverged()) {
      setDivergeOpen(true);
      return;
    }
    doCommit();
  };

  const resolveContractorIdForSave = async (): Promise<string | null> => {
    const fromScheme = appliedScheme?.contractorId || availableSchemes[0]?.contractorId;
    if (fromScheme) return fromScheme;
    if (!nipClean) return null;
    try {
      const c = (await axiosInstance.get("/contractors/find", { params: { nip: nipClean } })).data;
      return c?.Id ?? null;
    } catch {
      return null;
    }
  };

  // Zapis ręcznego dekretu jako nowy/aktualizowany schemat, następnie księgowanie.
  const saveDivergedScheme = async (mode: "add" | "update") => {
    setDivergeOpen(false);
    const contractorId = await resolveContractorIdForSave();
    const lines = decreeToSchemeLines();
    if (contractorId && lines.length) {
      saveScheme(
        {
          contractorId,
          direction: postingDirection,
          mode,
          schemeId: mode === "update" ? appliedScheme?.id : undefined,
          lines,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([SCHEMES_QUERY_KEY, contractorId]);
            queryClient.invalidateQueries([SCHEMES_RESOLVE_QUERY_KEY]);
            notification({
              title: "Schemat księgowania",
              text: mode === "add" ? "Zapisano nowy schemat kontrahenta." : "Zaktualizowano schemat kontrahenta.",
              type: ENotificationType.INFO,
            });
          },
          onError: () =>
            notification({
              title: "Schemat księgowania",
              text: "Nie udało się zapisać schematu.",
              type: ENotificationType.ERROR,
            }),
        },
      );
    }
    doCommit();
  };

  // Zastosuj schemat księgowania do wierszy dekretu: rozksięgowuje kwoty
  // z wiersza „do rozksięgowania" (netto/VAT/brutto) wg szablonu schematu.
  const applyScheme = (scheme: IPostingScheme) => {
    const netBase = amountToPost;
    const vatBase = invoiceVatTotal;
    const grossBase = netBase + vatBase;
    const baseVal = (b: PostingSchemeBase) =>
      b === "Vat" ? vatBase : b === "Gross" ? grossBase : netBase;
    const lines: DecreeLine[] = scheme.lines.map((l) => {
      const amt = Math.round(baseVal(l.base) * (l.factor ?? 1) * 100) / 100;
      const stored = amt ? amt.toFixed(2) : "";
      const found = wpkOptions.find((o) => o.code === l.account);
      return {
        id: crypto.randomUUID(),
        accountCode: l.account,
        accountDesc: found?.label ?? "",
        kwotaWn: l.side === "Dt" ? stored : "",
        kwotaMa: l.side === "Ct" ? stored : "",
        description: l.description ?? "",
      };
    });
    setDecreeLines(lines.length ? lines : [makeDecreeLine(), makeDecreeLine()]);
    setAppliedScheme(scheme);
    notification({
      title: "Dekretuj",
      text: `Zaproponowano dekret wg schematu${scheme.name ? ` „${scheme.name}"` : ""}.`,
      type: ENotificationType.INFO,
    });
  };

  // „Dekretuj": gdy jeden schemat — stosuje od razu; gdy kilka — menu wyboru.
  const handleAutoDecree = (e: React.MouseEvent<HTMLElement>) => {
    if (availableSchemes.length === 0) {
      notification({
        title: "Dekretuj",
        text: `Brak schematu księgowania dla kontrahenta (NIP ${nipClean || "—"}, ${postingDirection === "Sale" ? "sprzedaż" : "zakup"}).`,
        type: ENotificationType.ERROR,
      });
      return;
    }
    if (availableSchemes.length === 1) {
      applyScheme(availableSchemes[0]);
      return;
    }
    setSchemeMenuAnchor(e.currentTarget);
  };

  const fileLabel = documentType === "Receipt" ? "rachunku" : "faktury";
  const mainLabel = hideFileSection
    ? "Dane faktury"
    : documentType === "Receipt"
    ? "Wystawca i dane rachunku"
    : "Wystawca i dane faktury";

  return (
    <>
      {!hideFileSection && (
        <RadioGroup
          row
          value={documentType ?? ""}
          onChange={(e) => setDocumentType(e.target.value as RegistryInvoiceDocumentType)}
        >
          <FormControlLabel value="Invoice" control={<Radio size="small" />} label="Faktura zakupowa" />
          <FormControlLabel value="Receipt" control={<Radio size="small" />} label="Rachunek" />
        </RadioGroup>
      )}

      {documentType && (
        <>
          {/* Sekcja pliku — ukryta tylko w trybie „Księgowanie" (dane z faktury
              KSeF, bez skanu). Przy wejściu z podglądu (preloadedFile) sekcja jest
              widoczna, a plik jest już do niej załadowany; domyślnie zwinięta
              (formularz otwiera się na „Wystawca/Dane faktury"). */}
          {!hideFileSection && <CollapsibleSection
            title={`Dodaj plik ${fileLabel}`}
            isOpen={openSection === "file"}
            onToggle={() => toggleSection("file")}
            bodyClassName={styles.sectionBodyDense}
          >
            {!invoiceFile ? (
              <div
                {...getRootProps()}
                className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ""}`}
              >
                <input {...getInputProps()} />
                <FontAwesomeIcon icon={faPaperclip} className={styles.dropzoneIcon} />
                <span className={styles.dropzoneText}>
                  Przeciągnij plik lub{" "}
                  <span className={styles.dropzoneLink}>pobierz z dysku</span>
                </span>
                <span className={styles.dropzoneHint}>PDF, DOC, DOCX</span>
              </div>
            ) : (
              <div className={styles.filePreview}>
                <FontAwesomeIcon icon={faPaperclip} className={styles.fileIcon} />
                <span className={styles.fileName}>{invoiceFile.name}</span>
                <button
                  className={styles.filePreviewBtn}
                  onClick={() =>
                    addBlob(new Blob([invoiceFile], { type: invoiceFile.type }), invoiceFile.name)
                  }
                  title="Podgląd"
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>
                <button
                  className={styles.fileRemove}
                  onClick={() => {
                    setInvoiceFile(null);
                    removeBlob();
                  }}
                  title="Usuń plik"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            )}
          </CollapsibleSection>}

          {/* Wystawca + dane dokumentu (sekcje scalone, gęsty układ) */}
          <CollapsibleSection
            title={mainLabel}
            isOpen={openSection === "issuer"}
            onToggle={() => toggleSection("issuer")}
            bodyClassName={styles.sectionBodyDense}
            summary={
              <span className={styles.sectionSummary}>
                <span>
                  <span className={styles.sectionSummaryLabel}>Netto</span>
                  {fmtPLN(invoiceNetTotal)} zł
                </span>
                {documentType === "Invoice" && (
                  <span>
                    <span className={styles.sectionSummaryLabel}>VAT</span>
                    {fmtPLN(invoiceVatTotal)} zł
                  </span>
                )}
              </span>
            }
          >
            {documentType === "Invoice" ? (
              <>
                {/* Box „Wystawca" — jak boxy Sprzedawca/Nabywca w Fakturze
                    zakupowej: obramowany fieldset + legenda, pola standard. */}
                <fieldset className={styles.entityBox}>
                  <legend className={styles.entityLabel}>Wystawca</legend>
                  <TextField
                    label="NIP"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={issuerNIP}
                    onChange={(e) => setIssuerNIP(e.target.value)}
                    onBlur={(e) => handleNipBlur(e.target.value)}
                    {...pasteProps(setIssuerNIP)}
                    InputProps={{
                      inputComponent: NIPMaskedInput as React.ElementType,
                      ...(nipLoading && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ),
                      }),
                    }}
                  />
                  <TextField
                    label="Firma"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={issuerCompany}
                    onChange={(e) => setIssuerCompany(e.target.value)}
                    placeholder="Uzupełniane automatycznie z NIP"
                    {...pasteProps(setIssuerCompany)}
                  />
                  <TextField
                    label="Adres"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={issuerAddress}
                    onChange={(e) => setIssuerAddress(e.target.value)}
                    placeholder="Uzupełniane automatycznie z NIP"
                    {...pasteProps(setIssuerAddress)}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    variant="standard"
                    type="email"
                    value={issuerEmail}
                    onChange={(e) => setIssuerEmail(e.target.value)}
                    {...pasteProps(setIssuerEmail)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            title="Kopiuj email"
                            onClick={() => navigator.clipboard.writeText(issuerEmail).catch(() => {})}
                          >
                            <FontAwesomeIcon icon={faCopy} style={{ fontSize: 13 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </fieldset>
                {/* Numer konta — jak „Konto Sprzedawcy": osobne pole outlined */}
                <TextField
                  label="Numer konta"
                  size="small"
                  fullWidth
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  InputProps={{
                    inputComponent: AccountNumberMaskedInput as React.ElementType,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          title="Kopiuj numer konta"
                          onClick={() => navigator.clipboard.writeText(accountNumber).catch(() => {})}
                        >
                          <FontAwesomeIcon icon={faCopy} style={{ fontSize: 13 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <fieldset className={styles.entityBox}>
                <legend className={styles.entityLabel}>Wystawca</legend>
                <TextField
                  label="PESEL"
                  size="small"
                  fullWidth
                  variant="standard"
                  value={issuerPESEL}
                  onChange={(e) => setIssuerPESEL(e.target.value)}
                  {...pasteProps(setIssuerPESEL)}
                />
                <TextField
                  label="Imię i Nazwisko"
                  size="small"
                  fullWidth
                  variant="standard"
                  value={issuerFullName}
                  onChange={(e) => setIssuerFullName(e.target.value)}
                  {...pasteProps(setIssuerFullName)}
                />
                <TextField
                  label="Adres"
                  size="small"
                  fullWidth
                  variant="standard"
                  value={issuerAddress}
                  onChange={(e) => setIssuerAddress(e.target.value)}
                  {...pasteProps(setIssuerAddress)}
                />
                <TextField
                  label="Email"
                  size="small"
                  fullWidth
                  variant="standard"
                  type="email"
                  value={issuerEmail}
                  onChange={(e) => setIssuerEmail(e.target.value)}
                  {...pasteProps(setIssuerEmail)}
                />
              </fieldset>
            )}

            {/* Row 1 Invoice: Numer faktury | Waluta | Status */}
            {documentType === "Invoice" && (
              <div className={styles.row3}>
                <TextField
                  label="Numer faktury"
                  size="small"
                  fullWidth
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  {...pasteProps(setInvoiceNumber)}
                />
                <FormControl size="small" sx={{ width: 110, flexShrink: 0 }}>
                  <InputLabel>Waluta</InputLabel>
                  <Select
                    value={currency}
                    label="Waluta"
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value as RegistryInvoiceStatus | "")}
                  >
                    <MenuItem value=""><em>Brak</em></MenuItem>
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}

            <div className={styles.row3}>
              <DatePicker
                label="Data operacji"
                value={operationDate}
                onChange={(v) => setOperationDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="Data wystawienia"
                value={issueDate}
                onChange={(v) => setIssueDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="Data płatności"
                value={paymentDate}
                onChange={(v) => setPaymentDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </div>

            <TextField
              label="Przedmiot"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              {...pasteProps(setSubject)}
            />

            {/* Receipt status (no invoice number row for receipts) */}
            {documentType === "Receipt" && (
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value as RegistryInvoiceStatus | "")}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {documentType === "Invoice" ? (
              <>
                <div className={styles.sectionLabel}>Kwoty</div>
                {amountRows.map((row) => (
                  <div key={row.id} className={styles.amountRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <AmountField
                        label="Netto"
                        liveFormat
                        value={row.net}
                        onChange={(v) => updateRow(row.id, "net", v)}
                      />
                    </div>
                    <TextField
                      select
                      size="small"
                      label="Stawka"
                      value={row.vatRate}
                      onChange={(e) => updateRow(row.id, "vatRate", Number(e.target.value))}
                      sx={{
                        width: 80,
                        flexShrink: 0,
                      }}
                    >
                      {VAT_RATES.map((r) => (
                        <MenuItem key={r} value={r}>{r}%</MenuItem>
                      ))}
                    </TextField>
                    <ReadAmountField label="VAT" value={row.vat} sx={{ flex: 1, minWidth: 0 }} />
                    <ReadAmountField label="Brutto" value={row.gross} sx={{ flex: 1, minWidth: 0 }} />
                    {amountRows.length > 1 && (
                      <button
                        type="button"
                        className={styles.fileRemove}
                        onClick={() => removeRow(row.id)}
                        title="Usuń wiersz"
                        style={{ alignSelf: "center", marginTop: 2 }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className={styles.addPartyBtn} onClick={addRow}>
                  <FontAwesomeIcon icon={faPlus} /> Dodaj wiersz
                </button>
              </>
            ) : (
              <AmountField label="Kwota" liveFormat value={amount} onChange={setAmount} />
            )}
          </CollapsibleSection>

          {/* ── Dekretacja księgowa ───────────────────────────────────── */}
          <CollapsibleSection
            title="Dekretacja księgowa"
            isOpen={openSection === "decree"}
            onToggle={() => toggleSection("decree")}
            bodyClassName={styles.sectionBodyDense}
          >
            {/* Rodzaj dziennika (rejestru) — wybór przed księgowaniem. */}
            <FormControl size="small" sx={{ width: 260 }}>
              <InputLabel>Rodzaj dziennika</InputLabel>
              <Select
                value={journal}
                label="Rodzaj dziennika"
                onChange={(e) => setJournal(e.target.value)}
                disabled={isBooked}
              >
                {JOURNAL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Grupa „do rozksięgowania": Kwota netto (read-only) − suma dekretów Wn
                oraz VAT. Wartość netto ujemna (przeksięgowano) → czerwona ramka. */}
            <div className={styles.sectionLabel}>do rozksięgowania</div>
            <div className={styles.row2}>
              <ReadAmountField
                label="Kwota netto"
                value={fmtPLN(amountToPost)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  ...(amountToPost < 0 && {
                    "& .MuiOutlinedInput-root": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d32f2f" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d32f2f" },
                    "& .MuiInputLabel-root": { color: "#d32f2f" },
                  }),
                }}
              />
              <ReadAmountField
                label="VAT"
                value={fmtPLN(invoiceVatTotal)}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                className={styles.decreeSchemeBtn}
                title={
                  hasScheme
                    ? "Dekretuj wg schematu księgowania kontrahenta"
                    : "Brak schematu księgowania dla tego kontrahenta"
                }
                disabled={busy || isBooked || !hasScheme || schemesQuery.isFetching}
                onClick={handleAutoDecree}
              >
                Dekretuj
              </button>
            </div>

            {/* Tabela dekretów */}
            <div className={styles.decreeTable}>
              <div className={styles.decreeTableHead}>
                <div className={styles.decreeTdCode}>Nr kont</div>
                <div className={styles.decreeTdDesc}>Opis</div>
                <div className={styles.decreeTdAmt}>Konto Wn</div>
                <div className={styles.decreeTdAmt}>Konto Ma</div>
                <div className={styles.decreeTdBtn} />
              </div>
              {decreeLines.map((line) => (
                <div key={line.id} className={styles.decreeTableRow}>
                  {/* Kolumna 1: Nr kont — przeszukiwalna lista WPK */}
                  <div className={styles.decreeTdCode}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={wpkOptions}
                      filterOptions={wpkFilter}
                      getOptionLabel={(o) => (typeof o === "string" ? o : o.code)}
                      inputValue={line.accountCode}
                      onInputChange={(_, val) =>
                        updateDecreeLine(line.id, { accountCode: val, accountDesc: "" })
                      }
                      onChange={(_, val) => {
                        if (typeof val === "string") {
                          updateDecreeLine(line.id, { accountCode: val, accountDesc: "" });
                        } else if (val) {
                          updateDecreeLine(line.id, { accountCode: val.code, accountDesc: val.label });
                        }
                      }}
                      PopperComponent={WidePopper}
                      renderOption={(props, o) => (
                        <li {...props} key={o.id} style={{ display: "block" }} title={`${o.code} — ${o.label}`}>
                          <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {o.code} — {o.label}
                          </span>
                          {o.path !== o.label && (
                            <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
                              {o.path}
                            </span>
                          )}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Nr konta" title={wpkTooltip(line.accountCode)} />
                      )}
                    />
                  </div>
                  {/* Kolumna 2: Opis — auto z WPK */}
                  <div className={styles.decreeTdDesc}>
                    <span className={styles.decreeAccountDesc} title={line.accountDesc}>
                      {line.accountDesc || <span className={styles.decreeAccountDescEmpty}>—</span>}
                    </span>
                  </div>
                  {/* Kolumna 3: Konto Wn */}
                  <div className={styles.decreeTdAmt}>
                    <AmountField
                      label=""
                      liveFormat
                      value={line.kwotaWn}
                      onChange={(v) => updateDecreeLine(line.id, { kwotaWn: v })}
                    />
                  </div>
                  {/* Kolumna 4: Konto Ma */}
                  <div className={styles.decreeTdAmt}>
                    <AmountField
                      label=""
                      liveFormat
                      value={line.kwotaMa}
                      onChange={(v) => updateDecreeLine(line.id, { kwotaMa: v })}
                    />
                  </div>
                  {/* Przycisk Opis + opcjonalne Usuń */}
                  <div className={styles.decreeTdBtn}>
                    <button
                      type="button"
                      className={`${styles.decreeOpisBtn} ${line.description ? styles.decreeOpisBtnFilled : ""}`}
                      title={line.description || "Dodaj opis"}
                      onClick={(e) => setDescEditor({ id: line.id, anchor: e.currentTarget })}
                    >
                      Opis
                    </button>
                    {decreeLines.length > 1 && (
                      <button
                        type="button"
                        className={styles.fileRemove}
                        onClick={() => removeDecreeLine(line.id)}
                        title="Usuń wiersz"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pełny edytor opisu (textarea) — otwierany na życzenie z pola Opis. */}
            <Popover
              open={!!descEditor}
              anchorEl={descEditor?.anchor ?? null}
              onClose={() => setDescEditor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <div style={{ padding: 12, width: 380 }}>
                <TextField
                  label="Opis"
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  autoFocus
                  value={
                    descEditor
                      ? decreeLines.find((l) => l.id === descEditor.id)
                          ?.description ?? ""
                      : ""
                  }
                  onChange={(e) =>
                    descEditor &&
                    updateDecreeLine(descEditor.id, { description: e.target.value })
                  }
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 8,
                  }}
                >
                  <Button
                    size="small"
                    color="error"
                    startIcon={<FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />}
                    onClick={() => {
                      if (descEditor)
                        updateDecreeLine(descEditor.id, { description: "" });
                      setDescEditor(null);
                    }}
                  >
                    Usuń opis
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setDescEditor(null)}
                  >
                    Gotowe
                  </Button>
                </div>
              </div>
            </Popover>

            <button
              type="button"
              className={styles.addPartyBtn}
              onClick={addDecreeLine}
            >
              <FontAwesomeIcon icon={faPlus} /> Dodaj wiersz dekretu
            </button>

            <div className={styles.decreeBalance}>
              <span
                className={`${styles.decreeBalanceMsg} ${decreeBalanced ? styles.balOk : styles.balBad}`}
              >
                {decreeBalanced
                  ? "Dekret bilansuje się"
                  : `Różnica: ${fmtPLN(Math.abs(decreeSum.dt - decreeSum.ct))}`}
              </span>
              <span className={styles.decreeBalanceSum}>
                Suma Wn: <strong>{fmtPLN(decreeSum.dt)}</strong>
              </span>
              <span className={styles.decreeBalanceSum}>
                Suma Ma: <strong>{fmtPLN(decreeSum.ct)}</strong>
              </span>
            </div>

            {/* Pola dowodu księgowego — widoczne po zapisie do bufora lub
                zaksięgowaniu (isPosted). */}
            {isPosted && (
              <div className={styles.row2}>
                <DatePicker
                  label="Data księgowania"
                  value={postingDate}
                  onChange={(v) => setPostingDate(v)}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
                <TextField
                  label="Numer dowodu księgowego"
                  size="small"
                  fullWidth
                  value={accountingDocNo}
                  onChange={(e) => setAccountingDocNo(e.target.value)}
                  {...pasteProps(setAccountingDocNo)}
                />
              </div>
            )}

            {!isBooked ? (
              /* Operacje księgowania: Symulacja / Bufor / Zaksięguj */
              <div className={styles.decreePostRow}>
                {isPosted && (
                  <button
                    type="button"
                    className={styles.postUndoBtn}
                    onClick={() => setIsPosted(false)}
                  >
                    Cofnij księgowanie
                  </button>
                )}
                <button
                  type="button"
                  className={styles.postGhostBtn}
                  title="Symulacja księgowania"
                  disabled={busy}
                  onClick={handleSimulate}
                >
                  Symulacja
                </button>
                <button
                  type="button"
                  className={styles.postGhostBtn}
                  title="Bufor księgowania"
                  disabled={busy || !decreeBalanced || decreeSum.dt === 0}
                  onClick={handleBuffer}
                >
                  Bufor
                </button>
                <button
                  type="button"
                  className={styles.postBtn}
                  disabled={busy || !canSubmit || !decreeBalanced || decreeSum.dt === 0}
                  onClick={handleBook}
                >
                  Zaksięguj
                </button>
              </div>
            ) : (
              /* Po zaksięgowaniu: wpis w dzienniku + nawigacja procedury */
              <div className={styles.journalEntryPanel}>
                <div className={styles.journalEntryTitle}>
                  Wpis w dzienniku księgowania
                </div>
                <div className={styles.row3}>
                  <TextField
                    label="Rodzaj dziennika"
                    size="small"
                    fullWidth
                    value={postingResult?.journalName || postingResult?.journal || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Data wpisu"
                    size="small"
                    fullWidth
                    value={
                      postingResult?.entryDate
                        ? moment(postingResult.entryDate).format("DD-MM-YYYY")
                        : ""
                    }
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Numer wpisu"
                    size="small"
                    fullWidth
                    value={postingResult?.entryNumber ?? ""}
                    InputProps={{ readOnly: true }}
                  />
                </div>
                <div className={styles.decreePostRow}>
                  {onBookNext && (
                    <button
                      type="button"
                      className={styles.postGhostBtn}
                      onClick={onBookNext}
                    >
                      Następna faktura do księgowania
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.postBtn}
                    onClick={onClose}
                  >
                    Zakończ procedurę księgowania
                  </button>
                </div>
              </div>
            )}
          </CollapsibleSection>



        </>
      )}

      {/* Wybór schematu, gdy kontrahent ma ich kilka dla danego kierunku. */}
      <Popover
        open={!!schemeMenuAnchor}
        anchorEl={schemeMenuAnchor}
        onClose={() => setSchemeMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div style={{ padding: 6, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#666", padding: "4px 8px" }}>
            Wybierz schemat księgowania
          </div>
          {availableSchemes.map((s, i) => (
            <MenuItem
              key={s.id ?? i}
              onClick={() => {
                setSchemeMenuAnchor(null);
                applyScheme(s);
              }}
            >
              {s.isDefault ? "★ " : ""}
              {s.name || `Schemat ${i + 1}`}
            </MenuItem>
          ))}
        </div>
      </Popover>

      {/* Ręczny dekret różny od schematu — pytanie o zapis schematu. */}
      <Dialog open={divergeOpen} onClose={() => setDivergeOpen(false)}>
        <DialogTitle>Zapisać dekret jako schemat kontrahenta?</DialogTitle>
        <DialogContent>
          Wprowadzony dekret różni się od schematu księgowania. Czy zapisać go jako
          nowy schemat kontrahenta, czy zaktualizować dotychczasowy?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => saveDivergedScheme("add")}>Nowy schemat</Button>
          <Button
            onClick={() => saveDivergedScheme("update")}
            disabled={!appliedScheme && availableSchemes.length === 0}
          >
            Aktualizuj schemat
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setDivergeOpen(false);
              doCommit();
            }}
          >
            Księguj bez zapisu
          </Button>
        </DialogActions>
      </Dialog>

      {blob &&
        ReactDOM.createPortal(
          <div className={styles.pdfPreviewPanel} ref={pdfPanelRef}>
            <div className={`${styles.pdfSelectionBar} ${pdfSelection ? styles.pdfSelectionBarActive : ""}`}>
              {pdfSelection ? (
                <>
                  <span className={styles.pdfSelectionText}>
                    „{pdfSelection.length > 80 ? pdfSelection.slice(0, 80) + "…" : pdfSelection}"
                  </span>
                  <span className={styles.pdfSelectionHint}>← kliknij pole, by wkleić</span>
                </>
              ) : (
                <span className={styles.pdfSelectionPlaceholder}>
                  Zaznacz fragment tekstu w podglądzie, by wkleić w pole formularza
                </span>
              )}
            </div>
            <PDFViewer
              file={blob}
              title={documentType === "Receipt" ? "Podgląd rachunku" : "Podgląd faktury"}
              showCloseButton={true}
              onCloseButton={removeBlob}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default InvoiceRegistryForm;
